import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from 'react';
import { FoxyAvatar, type FoxyState } from './FoxyAvatar';
import { FoxyCelebration } from './FoxyCelebration';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { useAuth } from '@/hooks/use-auth';
import { Send, ThumbsUp, ThumbsDown, Check, X } from 'lucide-react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ChatMessage {
  readonly id: string;
  readonly role: 'user' | 'assistant';
  readonly content: string;
  readonly tier?: string;
  readonly needsConfirmation?: boolean;
  readonly toolCalls?: ReadonlyArray<{ name: string; input: Record<string, unknown> }>;
  readonly escalated?: boolean;
  readonly feedbackGiven?: 'up' | 'down' | null;
}

interface MilestoneData {
  readonly type: string;
  readonly message: string;
}

interface FoxyChatProps {
  readonly tenantId: string;
}

// ---------------------------------------------------------------------------
// API helpers
// ---------------------------------------------------------------------------

async function apiCall<T>(
  endpoint: string,
  body: unknown,
  accessToken: string | null,
): Promise<T> {
  const res = await fetch(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    },
    body: JSON.stringify(body),
  });
  const json = await res.json() as { success: boolean; data?: T; error?: string };
  if (!json.success) throw new Error(json.error ?? 'API error');
  return json.data as T;
}

let messageIdCounter = 0;
function nextId(): string {
  messageIdCounter += 1;
  return `msg-${messageIdCounter}-${Date.now()}`;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function FoxyChat({ tenantId }: FoxyChatProps): ReactNode {
  const { accessToken } = useAuth();
  const [messages, setMessages] = useState<readonly ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [foxyState, setFoxyState] = useState<FoxyState>('idle');
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [milestone, setMilestone] = useState<MilestoneData | null>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Send a message to Foxy
  const handleSend = useCallback(async (e?: FormEvent) => {
    e?.preventDefault();
    const text = inputValue.trim();
    if (!text || loading) return;

    setInputValue('');
    setError(null);
    setLoading(true);
    setFoxyState('thinking');

    // Add user message
    const userMsg: ChatMessage = { id: nextId(), role: 'user', content: text };
    setMessages((prev) => [...prev, userMsg]);

    try {
      const data = await apiCall<{
        response: string;
        tier: string;
        confidence: number;
        sessionId: string;
        needsConfirmation: boolean;
        escalated: boolean;
        toolCalls?: ReadonlyArray<{ name: string; input: Record<string, unknown> }>;
      }>('/api/foxy/chat', { message: text, tenantId, sessionId }, accessToken);

      setSessionId(data.sessionId);

      const assistantMsg: ChatMessage = {
        id: nextId(),
        role: 'assistant',
        content: data.response,
        tier: data.tier,
        needsConfirmation: data.needsConfirmation,
        ...(data.toolCalls ? { toolCalls: data.toolCalls } : {}),
        escalated: data.escalated,
        feedbackGiven: null,
      };

      setMessages((prev) => [...prev, assistantMsg]);
      setFoxyState(data.escalated ? 'peeking' : 'idle');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : '\u05DE\u05E9\u05D4\u05D5 \u05D4\u05E9\u05EA\u05D1\u05E9, \u05E0\u05E1\u05D5 \u05E9\u05D5\u05D1');
      setFoxyState('idle');
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  }, [inputValue, loading, tenantId, sessionId, accessToken]);

  // Confirm a tool action
  const handleConfirm = useCallback(async (msgId: string, toolCalls: ReadonlyArray<{ name: string; input: Record<string, unknown> }>) => {
    if (toolCalls.length === 0) return;
    setLoading(true);
    setFoxyState('thinking');

    try {
      const result = await apiCall<{ data?: unknown }>(
        '/api/foxy/confirm',
        { toolCall: toolCalls[0] },
        accessToken,
      );

      // Update the confirmation message to show success
      setMessages((prev) => prev.map((m) =>
        m.id === msgId
          ? { ...m, needsConfirmation: false, content: '\u05D4\u05E4\u05E2\u05D5\u05DC\u05D4 \u05D1\u05D5\u05E6\u05E2\u05D4 \u05D1\u05D4\u05E6\u05DC\u05D7\u05D4!' }
          : m
      ));
      setFoxyState('celebrating');
      setTimeout(() => setFoxyState('idle'), 2000);
    } catch {
      setError('\u05E9\u05D2\u05D9\u05D0\u05D4 \u05D1\u05D1\u05D9\u05E6\u05D5\u05E2 \u05D4\u05E4\u05E2\u05D5\u05DC\u05D4');
      setFoxyState('idle');
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  // Reject a tool action
  const handleReject = useCallback((msgId: string) => {
    setMessages((prev) => prev.map((m) =>
      m.id === msgId
        ? { ...m, needsConfirmation: false, content: '\u05D1\u05D9\u05D8\u05D5\u05DC. \u05D0\u05D9\u05DA \u05D0\u05E4\u05E9\u05E8 \u05DC\u05E2\u05D6\u05D5\u05E8 \u05D1\u05DE\u05E9\u05D4\u05D5 \u05D0\u05D7\u05E8?' }
        : m
    ));
  }, []);

  // Give feedback on a Foxy response
  const handleFeedback = useCallback(async (msgId: string, rating: 'up' | 'down') => {
    // Optimistic update
    setMessages((prev) => prev.map((m) =>
      m.id === msgId ? { ...m, feedbackGiven: rating } : m
    ));

    try {
      await apiCall('/api/foxy/feedback', { conversationId: msgId, rating }, accessToken);
    } catch {
      // Feedback is best-effort — don't show error
    }
  }, [accessToken]);

  const handleDismissMilestone = useCallback(() => {
    setMilestone(null);
  }, []);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="flex h-full flex-col" dir="rtl">
      {/* Header */}
      <div className="flex items-center gap-3 border-b border-border/60 px-4 py-3 bg-muted/20 backdrop-blur-sm">
        <FoxyAvatar state={foxyState} size={40} />
        <div className="flex-1 min-w-0">
          <h2 className="text-base font-bold leading-tight">{'\u05E4\u05D5\u05E7\u05E1\u05D9'}</h2>
          <p className="text-xs text-muted-foreground">
            {foxyState === 'thinking' ? '\u05D7\u05D5\u05E9\u05D1...' : '\u05DE\u05D5\u05DB\u05DF \u05DC\u05E2\u05D6\u05D5\u05E8'}
          </p>
        </div>
        <div className={`w-2 h-2 rounded-full transition-colors ${foxyState === 'thinking' ? 'bg-amber-400 animate-pulse' : 'bg-emerald-400'}`} />
      </div>

      {/* Messages area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full gap-5 text-center px-6">
            <FoxyAvatar state="idle" size={88} />
            <div>
              <p className="text-base font-semibold text-foreground mb-1">
                {'\u05E9\u05DC\u05D5\u05DD! \u05D0\u05E0\u05D9 \u05E4\u05D5\u05E7\u05E1\u05D9'}
              </p>
              <p className="text-sm text-muted-foreground">
                {'\u05D0\u05D9\u05DA \u05D0\u05E4\u05E9\u05E8 \u05DC\u05E2\u05D6\u05D5\u05E8 \u05D4\u05D9\u05D5\u05DD?'}
              </p>
            </div>
            <div className="flex flex-wrap justify-center gap-2 text-xs">
              {['\u05DE\u05D4 \u05D4\u05DE\u05DB\u05D9\u05E8\u05D5\u05EA \u05E9\u05DC\u05D9?', '\u05E1\u05DB\u05DD \u05D0\u05EA \u05D4\u05D9\u05D5\u05DD', '\u05D4\u05D6\u05DE\u05E0\u05D5\u05EA \u05D7\u05D3\u05E9\u05D4'].map((hint) => (
                <button
                  key={hint}
                  type="button"
                  onClick={() => { setInputValue(hint); inputRef.current?.focus(); }}
                  className="px-3 py-1.5 rounded-full bg-muted/60 border border-border/50 text-muted-foreground hover:bg-primary/10 hover:text-primary hover:border-primary/30 transition-colors cursor-pointer"
                >
                  {hint}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((msg) => (
          <div
            key={msg.id}
            className={cn(
              'flex gap-2 items-end',
              msg.role === 'user' ? 'justify-start' : 'justify-end',
            )}
          >
            {msg.role === 'assistant' && (
              <FoxyAvatar state="idle" size={28} className="shrink-0 mb-0.5" />
            )}
            <div className="max-w-[80%] space-y-1.5">
              <div
                className={cn(
                  'rounded-2xl px-4 py-2.5 text-sm leading-relaxed shadow-sm',
                  msg.role === 'user'
                    ? 'bg-primary text-primary-foreground rounded-br-sm'
                    : 'bg-muted/80 text-foreground rounded-bl-sm border border-border/40',
                )}
              >
                {msg.content}
              </div>

              {/* Confirmation buttons */}
              {msg.needsConfirmation && msg.toolCalls && (
                <div className="flex gap-2 mt-1.5">
                  <Button
                    size="sm"
                    onClick={() => handleConfirm(msg.id, msg.toolCalls!)}
                    disabled={loading}
                    className="min-h-[44px] shadow-sm"
                  >
                    <Check className="h-4 w-4 me-1" />
                    {'\u05D0\u05E9\u05E8'}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleReject(msg.id)}
                    className="min-h-[44px]"
                  >
                    <X className="h-4 w-4 me-1" />
                    {'\u05D1\u05D9\u05D8\u05D5\u05DC'}
                  </Button>
                </div>
              )}

              {/* Escalation notice */}
              {msg.escalated && (
                <p className="text-xs text-muted-foreground mt-1 px-1">
                  {'\u2709\uFE0F \u05D4\u05D5\u05E2\u05D1\u05E8 \u05DC\u05D0\u05DC\u05D9\u05D4'}
                </p>
              )}

              {/* Feedback buttons */}
              {msg.role === 'assistant' && !msg.needsConfirmation && msg.feedbackGiven === null && (
                <div className="flex gap-0.5 mt-0.5 ps-1">
                  <button
                    type="button"
                    onClick={() => handleFeedback(msg.id, 'up')}
                    className="p-1.5 rounded-full text-muted-foreground hover:text-emerald-500 hover:bg-emerald-500/10 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center cursor-pointer"
                    aria-label="thumbs up"
                  >
                    <ThumbsUp className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleFeedback(msg.id, 'down')}
                    className="p-1.5 rounded-full text-muted-foreground hover:text-red-500 hover:bg-red-500/10 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center cursor-pointer"
                    aria-label="thumbs down"
                  >
                    <ThumbsDown className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}

              {/* Feedback acknowledged */}
              {msg.feedbackGiven !== undefined && msg.feedbackGiven !== null && (
                <p className="text-xs text-muted-foreground mt-1 px-1">{'\u05EA\u05D5\u05D3\u05D4!'}</p>
              )}
            </div>
          </div>
        ))}

        {/* Typing indicator */}
        {loading && (
          <div className="flex gap-2 items-end justify-end">
            <FoxyAvatar state="thinking" size={28} className="shrink-0 mb-0.5" />
            <div className="bg-muted/80 border border-border/40 rounded-2xl rounded-bl-sm px-4 py-3 shadow-sm">
              <div className="flex gap-1.5 items-center">
                <span className="w-2 h-2 bg-muted-foreground/60 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-2 h-2 bg-muted-foreground/60 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="w-2 h-2 bg-muted-foreground/60 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Error message */}
      {error && (
        <div className="px-4 pb-2">
          <p className="text-sm text-destructive bg-destructive/10 rounded-lg px-3 py-2">{error}</p>
        </div>
      )}

      {/* Input area */}
      <form onSubmit={handleSend} className="flex gap-2 border-t border-border/60 p-3 bg-background/80 backdrop-blur-sm">
        <Input
          ref={inputRef}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          placeholder={'\u05E9\u05D0\u05DC\u05D5 \u05D0\u05EA \u05E4\u05D5\u05E7\u05E1\u05D9...'}
          dir="rtl"
          disabled={loading}
          className="flex-1 text-base min-h-[44px] bg-muted/30 border-border/50 focus:bg-background transition-colors"
          autoComplete="off"
        />
        <Button
          type="submit"
          size="icon"
          disabled={!inputValue.trim() || loading}
          className="min-h-[44px] min-w-[44px] shadow-sm"
        >
          <Send className="h-4 w-4" />
        </Button>
      </form>

      {/* Milestone celebration overlay */}
      <FoxyCelebration milestone={milestone} onDismiss={handleDismissMilestone} />
    </div>
  );
}
